import React, {ReactNode} from 'react'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {Text} from '@vio/atlas/src/components/designSystem/Text'
import {cssTheme} from '@vio/atlas/themes'
import {
  BookingStatus,
  Price as BookingPrice
} from '@vio/core/src/api-types/bovio/response/booking'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {
  AcceptedPaymentMode,
  hasPayAtPropertyPayment
} from '@vio/core/src/offer/business/offerPaymentMode'
import {OfferPrice} from '@vio/core/src/offer/types/offer'
import {ProviderTraderInformation} from '@vio/core/src/offer/types/ProviderTraderInformation'
import {CurrencyDisplayVariant} from '@vio/core/src/price/business/formatPrice'
import {
  getDisplayPrice,
  getHotelFeeElementByType,
  getPriceDetails
} from '@vio/core/src/price/business/price'
import {calculateDateDifference} from '@vio/core/src/utils/date'

import {getResidentVatRate} from '../../utils/PriceInformations/vat'
import {VatDisclaimer} from '../PriceInformation/ListElements/VatDisclaimer'
import {PriceBreakdown} from './PriceBreakdown'
import {PriceTotal} from './PriceTotal'
import {PaymentInformationSkeleton} from './Skeleton'

interface PriceDetailsProps {
  /** The Check in date of the offer */
  checkIn: string
  /** The Check out date of the offer */
  checkOut: string
  /** How the guest pays: now, at the property or later */
  paymentMode?: AcceptedPaymentMode | null
  /** Number of rooms */
  numberOfRooms: number
  /** Prices */
  prices: OfferPrice[] | BookingPrice[]
  /** Total number of guests */
  numberOfGuests: number
  /** Is loading status flag */
  isLoading?: boolean
  /** Whether to display the fees popups */
  showFeesDescriptions?: boolean
  /** Information about the trader and its terms and conditions */
  providerTraderInformation?: ProviderTraderInformation
  /**
    The status of the booking (confirmed, cancelled, frozen, freeze_cancelled, freeze_expired)
   */
  status?: BookingStatus
  /**
   * Whether to display the number of rooms in the PriceTotal component
   */
  displayNumberOfRooms?: boolean
  /**
   * Extra actions to perform on FeesDetails click
   */
  onFeesDetailsClick?: () => void

  /** How to display the currency in currency formatting. i.e. 'symbol', 'narrowSymbol', 'code', 'name', */
  currencyDisplayVariant?: CurrencyDisplayVariant

  /** Whether to display the discreet list of transparent fees */
  displayTransparentFees?: boolean

  /** Whether the offer price has additional charges */
  hasAdditionalCharges?: boolean
  /** The country code of the hotel required for VAT calculation */
  hotelCountryCode?: string
  /** Whether to display the taxes and fees combined - only relevant with displayTransparentFees enabled*/
  showTaxesAndFeesCombined?: boolean
  /** Optional data-id forwarded to the total price element. Used by external crawlers (e.g., GPAC) for price extraction. */
  priceTotalDataId?: string
  /** Whether the local VAT is included in the price */
  isLocalVatIncluded?: boolean
  /** Whether to show the "Price information" section with the VAT disclaimer */
  showVatDisclaimer?: boolean
  /** Explicit override for hiding the "with taxes & fees" message under the total. When undefined, falls back to `hasAdditionalCharges`. */
  hideTaxAndFeesMessage?: boolean
  /** Whether to show the nights, rooms and guests line under the total. When false, pay at property offers show "Pay at property" there instead. */
  showStaySummary?: boolean
  /** Replaces the total's currency figure, e.g. with the price in loyalty points */
  renderTotalPrice?: (value: number, currencyCode: string) => ReactNode
}

export const NUMBER_OF_DECIMALS_FOR_PRICE = 2

export enum FeeTypes {
  TaxesFees = 'taxes_fees',
  PayAtProperty = 'pay_at_property',
  ToCompleteTheBooking = 'to_complete_the_booking'
}

const PriceBreakdownWrapper = styled.div`
  padding: ${cssTheme.layout.spacing.s500} 0;
`

export const PriceDetails = ({
  checkIn,
  checkOut,
  numberOfGuests,
  numberOfRooms,
  paymentMode,
  prices,
  isLoading = false,
  showFeesDescriptions = true,
  displayNumberOfRooms = true,
  onFeesDetailsClick,
  currencyDisplayVariant,
  displayTransparentFees = false,
  hasAdditionalCharges = false,
  hotelCountryCode,
  showTaxesAndFeesCombined = false,
  priceTotalDataId,
  isLocalVatIncluded = false,
  showVatDisclaimer = false,
  hideTaxAndFeesMessage,
  showStaySummary,
  renderTotalPrice
}: PriceDetailsProps) => {
  const isPayAtProperty = hasPayAtPropertyPayment({paymentMode})

  if (isLoading) return <PaymentInformationSkeleton />
  const {totalDisplayPrice, displayCurrencyCode} = getPriceDetails(
    prices,
    isPayAtProperty
  )

  const localVatFee = getHotelFeeElementByType(
    'local_vat', // Replace with BreakdownType.LocalVat after backend is updated
    getDisplayPrice<OfferPrice | BookingPrice>(prices)
  ) as unknown as {
    // temporary casting till backend returns the correct type
    charge: {
      mode: string
      value: number
    }
  }

  /**
   * If fee is present in breakdown, we use the value of the fee
   * Otherwise, we use the resident VAT rate for the country
   * - fallback to country VAT rate for old flow and non-Israeli countries
   */
  const localVatAmount =
    localVatFee !== undefined
      ? localVatFee.charge.value
      : getResidentVatRate(hotelCountryCode || '')

  //if local VAT is present - always true, fallback to other eligibility methods if not
  const shouldIncludeVatInMessage =
    localVatFee !== undefined || isLocalVatIncluded

  const numberOfNights = calculateDateDifference(
    new Date(checkIn),
    new Date(checkOut)
  )

  return (
    <>
      <PriceBreakdownWrapper>
        <PriceBreakdown
          showFeesDescriptions={showFeesDescriptions}
          isPriceLoading={isLoading}
          onFeesDetailsClick={onFeesDetailsClick}
          currencyDisplayVariant={currencyDisplayVariant}
          prices={prices}
          isPayAtProperty={isPayAtProperty}
          displayTransparentFees={displayTransparentFees}
          numberOfRooms={numberOfRooms}
          numberOfNights={numberOfNights}
          displayNumberOfRooms={displayNumberOfRooms}
          showTaxesAndFeesCombined={showTaxesAndFeesCombined}
        />

        <PriceTotal
          isPriceLoading={isLoading}
          currencyCode={String(displayCurrencyCode)}
          totalPrice={Number(totalDisplayPrice)}
          rooms={numberOfRooms}
          nights={numberOfNights}
          guests={numberOfGuests}
          displayNumberOfRooms={displayNumberOfRooms}
          currencyDisplayVariant={currencyDisplayVariant}
          hideTaxAndFeesMessage={hideTaxAndFeesMessage ?? hasAdditionalCharges}
          showStaySummary={showStaySummary}
          isPayAtProperty={isPayAtProperty}
          dataId={priceTotalDataId}
          renderPrice={renderTotalPrice}
        />
        {showVatDisclaimer && (
          <>
            <Box paddingTop="s500" paddingBottom="s500">
              <Divider />
            </Box>
            <Text variant="titleM">
              <FormattedMessageWrapper
                id="priceInformation"
                defaultMessage="Price information"
              />
            </Text>
            <Box paddingTop="s200">
              <VatDisclaimer
                countryVat={localVatAmount}
                isLocalVatIncluded={shouldIncludeVatInMessage}
              />
            </Box>
          </>
        )}
      </PriceBreakdownWrapper>
    </>
  )
}
